import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { API_URL } from "../../../config";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Badge,
  Spinner,
  ListGroup,
  Accordion,
  Card as AccordionCard,
  Overlay,
  Popover,
} from "react-bootstrap";

// Add custom styles for the accordion
const accordionStyles = {
  moduleCard: {
    border: "1px solid #dee2e6",
    borderRadius: "8px",
    marginBottom: "10px",
    overflow: "hidden",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 4px rgba(0,0,0,0.03)",
  },
  moduleHeader: {
    padding: "0",
    background: "#f8f9fa",
    border: "none",
  },
  toggleButton: {
    width: "100%",
    textAlign: "left",
    padding: "15px 20px",
    color: "#333",
    fontWeight: "500",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "none",
    background: "transparent",
    transition: "all 0.2s ease",
  },
  chevron: {
    marginRight: "15px",
    transition: "transform 0.3s ease",
    fontSize: "14px",
  },
  chevronRotated: {
    marginRight: "15px",
    transition: "transform 0.3s ease",
    transform: "rotate(90deg)",
    fontSize: "14px",
  },
  moduleBody: {
    padding: "20px",
    borderTop: "1px solid #dee2e6",
  },
  moduleTitle: {
    fontWeight: "500",
    marginLeft: "10px",
  },
  moduleStats: {
    color: "#6c757d",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
  },
  lessonCount: {
    marginRight: "20px",
  },
  duration: {
    display: "flex",
    alignItems: "center",
  },
  clockIcon: {
    marginRight: "5px",
    fontSize: "14px",
  },
};

// Add feature card styles after accordion styles
const featureCardStyles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "30px",
  },
  card: {
    padding: "25px",
    borderRadius: "12px",
    background: "#fff",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    transition: "all 0.3s ease",
    border: "1px solid #eee",
    position: "relative",
    overflow: "hidden",
  },
  cardHover: {
    transform: "translateY(-5px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
  },
  numberCircle: {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    background: "#f8e7dc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "20px",
    color: "#333",
  },
  title: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "15px",
    color: "#333",
  },
  description: {
    color: "#666",
    marginBottom: "20px",
    lineHeight: "1.6",
  },
  link: {
    color: "#005dba",
    fontWeight: "500",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
  },
};

const CouseDetails = () => {
  const { course_id } = useParams();
  const college_id = useSelector((state) => state.college_id.college_id);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrollmentCount, setEnrollmentCount] = useState(0);
  const [courseModules, setCourseModules] = useState([]);
  const [activeKey, setActiveKey] = useState("0");
  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);
  const ref = useRef(null);
  const [copyMessage, setCopyMessage] = useState(false);

  const handleClick = (event) => {
    setTarget(event.target);
    setShow(!show);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(course?.embed_url ?? "");
    setCopyMessage(true);
    setTimeout(() => {
      setCopyMessage(false);
    }, 2000);
  };

  const shareOnFacebook = () => {
    const url = course?.embed_url ?? "";
    const message = course?.course_title || "Check out this course!";

    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        url,
      )}&quote=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  const shareOnWhatsApp = () => {
    const url = course?.embed_url ?? "";
    const message = course?.course_title || "Check out this course!";

    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(
        message,
      )}%20${encodeURIComponent(url)}`,
      "_blank",
    );
  };

  const shareOnLinkedIn = () => {
    const url = course?.embed_url ?? "";
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        url,
      )}`,
      "_blank",
    );
  };
  const fetchCourse = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/freshment_portal_apis/EduLearn/get_course_details_with_modules`,
        {
          params: {
            course_id: course_id,
            college_connect_id: college_id,
          },
        },
      );
      console.log(response);
      const data = response.data?.data?.course || {};
      setCourse(data);
      setCourseModules(response.data?.data?.modules || []);

      setEnrollmentCount(Math.floor(Math.random() * 200000) + 5000);
    } catch (error) {
      console.error("Error fetching course details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourse();
  }, [course_id]);

  const handleAccordionToggle = (eventKey) => {
    setActiveKey(activeKey === eventKey ? null : eventKey);
  };

  const generateBgColor = () => {
    const colors = ["#005dba", "#4ECDC4", "#7209B7", "#F9C74F"];
    const index = Number(course_id) % colors.length;
    return colors[index];
  };

  const bgColor = generateBgColor();

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading course details...</p>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="text-center py-5">
        <h3>Course not found</h3>
        <Link to="/courses" className="btn btn-primary mt-3">
          Back to Courses
        </Link>
      </div>
    );
  }

  const tags = course.tags
    ? course.tags.split(",").map((tag) => tag.trim())
    : [];

  return (
    <div>
      <div
        className="course-banner"
        style={{
          position: "relative",
          background: `linear-gradient(120deg, ${bgColor}30 0%, ${bgColor}20 100%)`,
          padding: "60px 0 80px",
          borderRadius: "20px",
          overflow: "hidden",
        }}
      >
        {/* Background Shapes */}
        <div
          className="shape-1"
          style={{
            background: `${bgColor}30`,
            position: "absolute",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            top: "-80px",
            left: "10%",
            zIndex: "0",
          }}
        ></div>
        <div
          className="shape-2"
          style={{
            position: "absolute",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            background: `${bgColor}30`,
            bottom: "-200px",
            right: "-100px",
            zIndex: "0",
          }}
        ></div>

        <Container className="position-relative" style={{ zIndex: "1" }}>
          <Row className="align-items-start">
            <Col lg={8} md={7} sm={12}>
              <h1 className="display-4 fw-bold mb-4" style={{ color: "#333" }}>
                {course.course_title}
              </h1>
              <div className="d-flex align-items-center mb-4">
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "15px",
                    border: "2px solid white",
                    borderRadius: "50%",
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  {course.thumbnail_url ? (
                    <img
                      src={course.thumbnail_url}
                      alt={course.provider_name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background: bgColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "100%",
                      }}
                    >
                      <span className="text-white fs-4">
                        {course.provider_name
                          ? course.provider_name.charAt(0)
                          : "C"}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <p className="mb-0 text-muted">with</p>
                  <h5 className="mb-0">{course.provider_name}</h5>
                </div>
                <div className="ms-4 ps-4 border-start">
                  <p className="mb-0 text-muted">Course level:</p>
                  <div className="d-flex align-items-center">
                    <span className="me-2">{course.difficulty_level}</span>
                    <div
                      style={{
                        width: "50px",
                        height: "8px",
                        backgroundColor: "#e9ecef",
                        borderRadius: "4px",
                      }}
                    >
                      <div
                        style={{
                          width:
                            course.difficulty_level === "Beginner"
                              ? "33%"
                              : course.difficulty_level === "Intermediate"
                                ? "66%"
                                : "100%",
                          height: "100%",
                          backgroundColor: bgColor,
                          borderRadius: "4px",
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="lead mb-4" style={{ maxWidth: "90%" }}>
                {course.description}
              </p>

              <div className="d-flex flex-wrap">
                {tags.map((tag, index) => (
                  <Badge
                    key={index}
                    bg="white"
                    text="dark"
                    className="me-2 mb-2 text-black"
                    style={{
                      borderRadius: "20px",
                      padding: "8px 16px",
                      fontSize: "14px",
                      boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                    }}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>

              <div ref={ref} className="mt-4">
                <Button
                  variant=""
                  size="sm"
                  onClick={handleClick}
                  className="btn btn-sm btn-secondary  position-relative"
                >
                  <i className="fas fa-share-alt me-2"></i>Share
                </Button>

                <Overlay
                  show={show}
                  target={target}
                  placement="top-start"
                  container={ref.current}
                  rootClose={true}
                  onHide={() => setShow(false)}
                >
                  <Popover
                    id="popover-basic"
                    className="popover-custom"
                    style={{ marginBottom: "10px", marginLeft: "10px" }}
                  >
                    <Popover.Content className="d-flex align-items-center p-3">
                      <div
                        onClick={copyLink}
                        className="copy-div fs-18 me-3 text-dark cursor-pointer"
                      >
                        <i className="fas fa-copy me-2 fs-18 icon-copy"></i>
                        Copy Link
                        {copyMessage && (
                          <span
                            style={{ zIndex: 10 }}
                            className="copy-message fade-in-out"
                          >
                            Link copied!
                          </span>
                        )}
                      </div>
                      <div
                        onClick={shareOnFacebook}
                        className="me-3 cursor-pointer"
                      >
                        <i className="fab fa-facebook-f me-2 fs-18 icon-facebook"></i>
                      </div>
                      <div
                        onClick={shareOnWhatsApp}
                        className="me-3 cursor-pointer"
                      >
                        <i className="fab fa-whatsapp me-2 fs-18 icon-whatsapp"></i>
                      </div>
                      <div onClick={shareOnLinkedIn} className="cursor-pointer">
                        <i className="fab fa-linkedin me-2 fs-18 icon-linkedin"></i>
                      </div>
                    </Popover.Content>
                  </Popover>
                </Overlay>
              </div>
            </Col>

            <Col lg={4} md={5} sm={12} className="mt-4 mt-md-0">
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Button
                    as={"a"}
                    href={course?.embed_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="primary"
                    size="lg"
                    className="w-100 mb-3 bg-primary text-white"
                    style={{
                      borderRadius: "8px",
                      padding: "12px",
                    }}
                  >
                    Enroll For Free
                  </Button>

                  <hr className="my-3" />

                  <ListGroup variant="flush">
                    <ListGroup.Item className="border-0 px-0 py-2">
                      <i className="fas fa-check-circle text-success me-2"></i>
                      Access to all course materials
                    </ListGroup.Item>
                    <ListGroup.Item className="border-0 px-0 py-2">
                      <i className="fas fa-check-circle text-success me-2"></i>
                      Interactive hands-on content
                    </ListGroup.Item>
                    <ListGroup.Item className="border-0 px-0 py-2">
                      <i className="fas fa-check-circle text-success me-2"></i>
                      {course.duration} of learning content
                    </ListGroup.Item>
                    {course.language && (
                      <ListGroup.Item className="border-0 px-0 py-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        Course language: {course.language}
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Course Content Section */}
      {courseModules && courseModules.length > 0 && (
        <div className="mt-5">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Course Content</h2>
          </div>

          <Row>
            <Col
              lg={course.source_provider_id == 1 ? 8 : 12}
              md={course.source_provider_id == 1 ? 7 : 12}
              sm={12}>
              {courseModules && courseModules.length > 0 ? (
                <Accordion
                  className="course-modules"
                  activeKey={activeKey}
                  onSelect={(key) => handleAccordionToggle(key)}
                >
                  {courseModules.map((module, index) => (
                    <AccordionCard key={index} style={accordionStyles.moduleCard}>
                      <AccordionCard.Header style={accordionStyles.moduleHeader}>
                        <Accordion.Toggle
                          as={Button}
                          variant="link"
                          eventKey={String(index)}
                          style={accordionStyles.toggleButton}
                          className="accordion-module-toggle"
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <i
                              className="fas fa-chevron-right"
                              style={{
                                ...accordionStyles.chevron,
                                transform:
                                  activeKey === String(index)
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                              }}
                            ></i>
                            <span style={accordionStyles.moduleTitle}>
                              {module.module_title}
                            </span>
                          </div>
                          <div style={accordionStyles.moduleStats}>
                            {module.lessons_count && (
                              <span style={accordionStyles.lessonCount}>
                                <i className="fas fa-book-open me-2"></i>
                                {module.lessons_count} lessons
                              </span>
                            )}
                            <span style={accordionStyles.duration}>
                              <i
                                className="far fa-clock me-2"
                                style={accordionStyles.clockIcon}
                              ></i>
                              {module.duration}
                            </span>
                          </div>
                        </Accordion.Toggle>
                      </AccordionCard.Header>
                      <Accordion.Collapse eventKey={String(index)}>
                        <AccordionCard.Body style={accordionStyles.moduleBody}>
                          <p className="mb-3">{module.module_description}</p>

                          {module.embed_url && (
                            <div className="d-flex justify-content-end mt-4">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                href={module.embed_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i className="fas fa-external-link-alt me-2"></i>
                                Preview Module
                              </Button>
                            </div>
                          )}
                        </AccordionCard.Body>
                      </Accordion.Collapse>
                    </AccordionCard>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-5">
                  <p>No course modules available yet.</p>
                  <Button
                    variant="primary btn-sm"
                    href={course?.embed_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Preview Course
                  </Button>
                </div>
              )}
            </Col>

            {course.source_provider_id == 1 &&
              <Col lg={4} md={5} sm={12} className="mt-4 mt-md-0">
                <div style={featureCardStyles.container}>
                  {/* Feature Card 1 */}
                  <div style={featureCardStyles.card} className="feature-card">
                    <div style={featureCardStyles.numberCircle}>1</div>
                    <h3 style={featureCardStyles.title}>
                      Learn faster with interactive screencasts
                    </h3>
                    <p style={featureCardStyles.description}>
                      Our interactive screencasts let you edit the code whenever you
                      want, just as if you were pair programming with the teacher.
                      As a result, you will code more and learn faster. Give it a
                      test drive.
                    </p>
                    <a
                      href="https://v1.scrimba.com/allcourses?price=free"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={featureCardStyles.link}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = "none";
                      }}
                    >
                      Give it a test drive{" "}
                      <i className="fas fa-arrow-right ms-2"></i>
                    </a>
                  </div>

                  {/* Feature Card 3 */}
                  <div style={featureCardStyles.card} className="feature-card">
                    <div style={featureCardStyles.numberCircle}>2</div>
                    <h3 style={featureCardStyles.title}>
                      Hack your way to your first developer job
                    </h3>
                    <p style={featureCardStyles.description}>
                      In addition to web development, our career path teaches you
                      how to get your first job. You will build a portfolio, improve
                      your LinkedIn, practice coding interview questions, and much
                      more.
                    </p>
                    <a
                      href="https://v1.scrimba.com/allcourses?price=free"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={featureCardStyles.link}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = "none";
                      }}
                    >
                      Join our community <i className="fas fa-arrow-right ms-2"></i>
                    </a>
                  </div>

                  {/* Feature Card 2 */}
                  <div style={featureCardStyles.card} className="feature-card">
                    <div style={featureCardStyles.numberCircle}>3</div>
                    <h3 style={featureCardStyles.title}>
                      Meet the world&apos;s best coding teachers
                    </h3>
                    <p style={featureCardStyles.description}>
                      We have handpicked the best teachers for each subject, and
                      combined them into one experience. You get to code alongside
                      experts like{" "}
                      <a
                        href="https://www.youtube.com/channel/UCVyRiMvfUNMA1UPlDPzG5Ow"
                        target="_blank"
                        className="text-decoration-underline"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#005dba";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "inherit";
                        }}
                      >
                        Gary Simon
                      </a>
                      ,{" "}
                      <a
                        href="https://x.com/cassidoo"
                        target="_blank"
                        className="text-decoration-underline"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#005dba";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "inherit";
                        }}
                      >
                        Cassidy Williams
                      </a>
                      , and{" "}
                      <a
                        href="https://www.youtube.com/kevinpowell"
                        target="_blank"
                        className="text-decoration-underline"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#005dba";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "inherit";
                        }}
                      >
                        Kevin Powell
                      </a>
                      , who have a combined following of over one million students.
                    </p>
                    {/* <a
                  href=""
                  style={featureCardStyles.link}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = "underline";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = "none";
                  }}
                >
                  See example projects{" "}
                  <i className="fas fa-arrow-right ms-2"></i>
                </a> */}
                  </div>
                </div>
              </Col>
            }

          </Row>
        </div>
      )}

      <style jsx="true">{`
        .popover-custom {
          border: none;
          border-radius: 10px;
          box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
          background-color: #fff;
        }
        .icon-copy {
          color: #007bff; /* Blue for copy link */
        }
        .icon-facebook {
          color: #4267b2; /* Facebook blue */
        }
        .icon-whatsapp {
          color: #25d366; /* WhatsApp green */
        }
        .icon-linkedin {
          color: #0077b5; /* LinkedIn blue */
        }
        .copy-div:hover {
          text-decoration: none;
          opacity: 0.8;
        }
        .copy-division {
          position: relative;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .copy-message {
          position: absolute;
          top: -30px; /* Adjust position above the icon */
          left: 10px;
          background-color: black;
          color: white;
          padding: 5px 8px;
          border-radius: 5px;
          font-size: 14px;
          white-space: nowrap; /* Prevent text wrap */
          opacity: 0; /* Start hidden */
          transition: opacity 0.5s ease;
        }
        .copy-message.fade-in-out {
          opacity: 1; /* Show the message */
        }
      `}</style>
    </div>
  );
};

export default CouseDetails;
